import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { bff } from '../lib/bff-client'
import type { TenantSapp, SappToggleResponse } from '../types/bff'

export function TenantPage() {
  const { tenantId } = useParams({ from: '/tenant/$tenantId' })
  const qc = useQueryClient()

  const tenantQ = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => bff.getTenant(tenantId),
  })

  const sappsQ = useQuery({
    queryKey: ['tenant-sapps', tenantId],
    queryFn: () => bff.getTenantSapps(tenantId),
  })

  const toggleMut = useMutation({
    mutationFn: ({ slug, enabled }: { slug: string; enabled: boolean }) =>
      bff.toggleSapp(tenantId, slug, enabled),
    onSuccess: (data) => {
      // Optimistic: invalidate both list caches
      qc.invalidateQueries({ queryKey: ['tenant-sapps', tenantId] })
      qc.invalidateQueries({ queryKey: ['tenant-health-card', tenantId] })
      // If dual-write partial (legacy_error set), surface to user
      if (data.legacy_error) {
        console.warn('Dual-write drift detected', data)
      }
    },
  })

  if (tenantQ.isLoading) return <div className="text-slate-400">Cargando tenant…</div>
  if (tenantQ.error) return <ErrorBox error={tenantQ.error} />

  const tenant = tenantQ.data!

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/" className="text-blue-600 hover:underline">
          ← Tenants
        </Link>
      </nav>

      <div>
        <h1 className="text-2xl font-bold">{tenant.name}</h1>
        <div className="mt-1 font-mono text-xs text-slate-500">{tenant.id}</div>
        <div className="mt-3 flex gap-3 text-sm">
          <Badge label="status" value={tenant.status} />
          <Badge label="bandeja" value={tenant.bandeja_app} />
          <Badge label="default_sapp" value={tenant.default_sapp ?? '—'} />
        </div>
      </div>

      <section>
        <h2 className="mb-3 text-lg font-semibold">S-Apps catalog</h2>

        {sappsQ.isLoading && <div className="text-slate-400">Cargando sapps…</div>}
        {sappsQ.error && <ErrorBox error={sappsQ.error} />}

        {sappsQ.data && (
          <div className="space-y-2">
            {sappsQ.data.map((s) => (
              <SappRow
                key={s.sapp_slug}
                sapp={s}
                onToggle={(enabled) => toggleMut.mutate({ slug: s.sapp_slug, enabled })}
                pending={toggleMut.isPending && toggleMut.variables?.slug === s.sapp_slug}
              />
            ))}
          </div>
        )}

        {toggleMut.data && <ToggleResultBanner data={toggleMut.data} />}
      </section>

      <section>
        <h2 className="mb-2 text-lg font-semibold">config_overrides</h2>
        <pre className="overflow-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
          {JSON.stringify(tenant.config_overrides, null, 2)}
        </pre>
      </section>
    </div>
  )
}

function Badge({ label, value }: { label: string; value: string }) {
  return (
    <span className="rounded bg-slate-100 px-2 py-0.5">
      <span className="text-slate-500">{label}:</span> <strong>{value}</strong>
    </span>
  )
}

function SappRow({
  sapp,
  onToggle,
  pending,
}: {
  sapp: TenantSapp
  onToggle: (enabled: boolean) => void
  pending: boolean
}) {
  return (
    <div className="flex items-center justify-between rounded border bg-white p-3">
      <div>
        <div className="font-medium">{sapp.sapp_slug}</div>
        <div className="text-xs text-slate-500">{sapp.description}</div>
        <div className="mt-1 flex gap-2 text-xs text-slate-400">
          <span>vertical: {sapp.vertical}</span>
          <span>v{sapp.version}</span>
          {sapp.meta_flow_id && <span>flow: {sapp.meta_flow_id}</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onToggle(!sapp.enabled)}
        disabled={pending}
        className={`rounded px-3 py-1.5 text-sm font-medium transition ${
          sapp.enabled
            ? 'bg-green-100 text-green-800 hover:bg-green-200'
            : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
        } disabled:opacity-50`}
      >
        {pending ? '…' : sapp.enabled ? 'enabled' : 'disabled'}
      </button>
    </div>
  )
}

function ToggleResultBanner({ data }: { data: SappToggleResponse }) {
  const ok = data.platform_updated && data.legacy_updated
  return (
    <div
      className={`mt-4 rounded border p-3 text-sm ${
        ok ? 'border-green-200 bg-green-50 text-green-900' : 'border-amber-300 bg-amber-50 text-amber-900'
      }`}
    >
      <div className="font-medium">
        {ok ? '✓ Dual-write OK' : '⚠ Partial write — drift detected'}
      </div>
      <ul className="mt-1 space-y-0.5 text-xs">
        <li>platform_updated: {String(data.platform_updated)}</li>
        <li>legacy_updated: {String(data.legacy_updated)}</li>
        <li>cache_invalidated: {String(data.cache_invalidated)}</li>
        {data.legacy_sessions_reset != null && (
          <li>legacy_sessions_reset: {data.legacy_sessions_reset}</li>
        )}
        {data.legacy_error && <li className="font-mono">legacy_error: {data.legacy_error}</li>}
      </ul>
    </div>
  )
}

function ErrorBox({ error }: { error: unknown }) {
  return (
    <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      Error: {error instanceof Error ? error.message : 'unknown'}
    </div>
  )
}
